import React, { useRef, useEffect, useCallback } from 'react'
import { Wrapper, Status } from '@googlemaps/react-wrapper'

import useCurrentLocation from 'hooks/useCurrentLocation'
import { useCurrentLocale } from 'context/LocaleContext'
import useForceUpdate from 'hooks/useForceUpdate'

import { GeoLocation, Bounds } from 'core/Geolocation'

import { GOOGLE_MAPS_DEFAULT_ZOOM, CONFIG } from 'configuration'

import Error from './Error'

export interface ExtendedMarkerOptions extends google.maps.MarkerOptions {
  id: string
  color?: string
}

type MarkerMap = { [key: string]: google.maps.Marker }

interface MapProps {
  center: google.maps.LatLngLiteral
  zoom: number
  markers: ExtendedMarkerOptions[]
  onBoundsUpdated?: (bounds: Bounds) => void
  onInfoWindowClick: (markerId: string) => void
}

// Loading spinner while getting location
/* const Loading: React.FC = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <IonSpinner />
  </div>
) */
const Loading: React.FC = () => null

// TODO set maximum zoom
// TODO hide closing blade in info window (through CSS) and programatically
// close it when the user clicks on the map
const Map: React.FC<MapProps> = (props) => {
  const { center, zoom, markers, onBoundsUpdated, onInfoWindowClick } = props

  const mapRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<MarkerMap>({})
  const infoWindowRef = useRef<google.maps.InfoWindow>(
    new google.maps.InfoWindow(),
  )
  const currentMarkerRef = useRef<string>('')
  const ref = useRef<HTMLDivElement>(null)

  const currentLocationMarkerRef = useRef<google.maps.Marker>(
    new google.maps.Marker({
      // NOTE show above all other markers
      zIndex: 1000,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        // NOTE default google maps color for current location
        fillColor: '#4285f4',
        fillOpacity: 1,
        strokeColor: '#fff',
        // strokeOpacity,
        strokeWeight: 2,
      },
    }),
  )
  const accuracyCircleRef = useRef<google.maps.Circle | null>(
    new google.maps.Circle({
      fillColor: '#61a0bf',
      fillOpacity: 0.4,
      //radius: errorRange,
      strokeColor: '#1bb6ff',
      strokeOpacity: 0.4,
      strokeWeight: 1,
      zIndex: 1,
    }),
  )

  const setCurrentLocation = useCallback((location: GeoLocation) => {
    const { latitude: lat, longitude: lng, accuracy } = location
    currentLocationMarkerRef.current.setPosition({ lat, lng })
    accuracyCircleRef.current?.setCenter({ lat, lng })
    accuracy && accuracyCircleRef.current?.setRadius(accuracy)
  }, [])

  const { watchLocation } = useCurrentLocation(setCurrentLocation)

  const forceUpdate = useForceUpdate()

  useEffect(() => {
    if (ref.current && !mapRef.current) {
      mapRef.current = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        clickableIcons: false,
        controlSize: 24,
        // NOTE disable all controls but the terrain/satellite switch
        fullscreenControl: false,
        zoomControl: false,
        streetViewControl: false,
        // mapTypeControl: true,
        panControl: false,
        scaleControl: false, // NOTE disabled by default
        rotateControl: false,
        keyboardShortcuts: false,
        // disableDefaultUI: true, // NOTE disable all controls
      })

      currentLocationMarkerRef.current.setMap(mapRef.current)
      accuracyCircleRef.current?.setMap(mapRef.current)

      // Set initial position from center (which comes from MapWrapper's location)
      currentLocationMarkerRef.current.setPosition(center)
      accuracyCircleRef.current?.setCenter(center)

      watchLocation()
    }
  }, [center, zoom, watchLocation])

  // Helper function to update bounds with safety checks
  const updateBoundsSafe = useCallback(() => {
    if (!mapRef.current || !onBoundsUpdated) return

    const bounds = mapRef.current.getBounds()

    if (!bounds) {
      // Android WebView: reintentar en el siguiente frame
      requestAnimationFrame(updateBoundsSafe)
      return
    }

    const mapBounds = bounds.toJSON()

    // NOTE construct the polygon, starting and finishing in the same point,
    // otherwise the query will not work properly
    // Reduced padding for mobile (0.0001 instead of 0.1)
    const safePadding = 0.0001

    const polygon: Bounds = [
      [mapBounds.west - safePadding, mapBounds.north + safePadding],
      [mapBounds.east + safePadding, mapBounds.north + safePadding],
      [mapBounds.east + safePadding, mapBounds.south - safePadding],
      [mapBounds.west - safePadding, mapBounds.south - safePadding],
      [mapBounds.west - safePadding, mapBounds.north + safePadding],
    ]

    onBoundsUpdated(polygon)
  }, [onBoundsUpdated])

  // Set up bounds listeners (optimized: only idle and dragend)
  useEffect(() => {
    if (!mapRef.current || !onBoundsUpdated) return

    const map = mapRef.current

    const idleListener = map.addListener('idle', updateBoundsSafe)
    const dragListener = map.addListener('dragend', updateBoundsSafe)

    // Forzar primera carga (Android lo necesita)
    setTimeout(updateBoundsSafe, 300)

    return () => {
      google.maps.event.removeListener(idleListener)
      google.maps.event.removeListener(dragListener)
    }
  }, [updateBoundsSafe, onBoundsUpdated])

  // markers
  useEffect(() => {
    if (!mapRef.current) return

    const currentMarkers = Object.keys(markersRef.current)
    const newMarkers = markers.map((marker) => marker.id)

    // NOTE hide markers no longer in view
    currentMarkers.forEach((id) => {
      if (!newMarkers.includes(id)) {
        markersRef.current[id].setMap(null)
      }
    })

    // NOTE add markers, reusing already existing ones
    markersRef.current = markers.reduce((acc: MarkerMap, marker) => {
      const { id, color, ...options } = marker

      if (acc[id]) acc[id].setMap(mapRef.current)
      else {
        acc[id] = new google.maps.Marker({
          ...options,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 8.5,
            fillColor: color,
            fillOpacity: 1,
            // strokeColor,
            // strokeOpacity,
            strokeWeight: 1,
          },
          map: mapRef.current,
        })

        acc[id].addListener('click', () => {
          currentMarkerRef.current = id

          infoWindowRef.current.setContent(options.title)
          infoWindowRef.current.open({
            map: mapRef.current,
            anchor: acc[id],
            shouldFocus: false,
          })

          // NOTE force update the component to add the click listener to the
          // info window (see effect below)
          forceUpdate()
        })
      }

      return acc
    }, markersRef.current)
  }, [markers, forceUpdate])

  // NOTE remove all markers on component unmount
  useEffect(() => {
    return () => {
      Object.keys(markersRef.current).forEach((id) =>
        markersRef.current[id].setMap(null),
      )
    }
  }, [])

  // info window
  useEffect(() => {
    infoWindowRef.current.addListener('closeclick', () => {
      // NOTE force update after the info window is dismissed to trigger the
      // effect below, so that closing and opening the same info window does
      // re-attach the click handler
      forceUpdate()
    })
    // NOTE we know `forceUpdate` will never change, so rest assured
  }, [forceUpdate])

  // HACK `InfoWindow` interface does not define a `getMap` method, although it
  // is available to it, and it is the only reliable way to check whether the
  // info window is opened
  const isInfoWindowOpen = Boolean((infoWindowRef.current as any).getMap())
  const infoWindowContent = infoWindowRef.current.getContent()

  // NOTE even though `InfoWindow` exposes a `addListener` method, it does not
  // trigger `click` events, so we need to add directly to its HTML content
  useEffect(() => {
    const infoWindow = document.querySelector('[role=dialog]')

    if (infoWindow) {
      infoWindow.addEventListener('click', (ev) => {
        const target = ev.target as HTMLElement

        // NOTE skip clicks in closing blade
        if (target && target.tagName === 'BUTTON') return

        onInfoWindowClick(currentMarkerRef.current)
      })
    }
  }, [isInfoWindowOpen, infoWindowContent, onInfoWindowClick])

  return <div ref={ref} style={{ height: '100%' }} />
}

const render = (status: Status) => {
  if (status === Status.FAILURE) return <Error />

  return <Loading />
}

interface MapWrapperProps {
  onLoad?: () => void
  markers: ExtendedMarkerOptions[]
  onBoundsUpdated?: (bounds: Bounds) => void
  onInfoWindowClick: (markerId: string) => void
}

// TODO handle loading states and errors for both @googlemaps/react-wrapper and
// useCurrentLocation
const MapWrapper: React.FC<MapWrapperProps> = (props) => {
  const { onLoad, ...rest } = props

  const locale = useCurrentLocale()
  const [language, region] = locale.split('-')

  const { location, error, refetch } = useCurrentLocation()

  const handleLoad = useCallback(
    (status: Status) => {
      if (status === Status.SUCCESS && onLoad) {
        onLoad()
      }
    },
    [onLoad],
  )

  // Use refetch on mount to force getting location even on remount
  useEffect(() => {
    refetch()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <Error />

  // Show loading while getting location
  if (!location) {
    return <Loading />
  }

  return (
    <Wrapper
      apiKey={CONFIG.GOOGLE_MAPS_API_KEY}
      render={render}
      language={language}
      region={region}
      callback={handleLoad}
    >
      <Map
        zoom={GOOGLE_MAPS_DEFAULT_ZOOM}
        center={{
          lat: location.latitude,
          lng: location.longitude,
        }}
        {...rest}
      />
    </Wrapper>
  )
}

export default MapWrapper
